// ------------------------------
// Example
// ------------------------------
/*
[
  {
    "drug": "simvastatin",
    "auc_ratio": 8.47,
    "auc_interval": [ 4, 13 ],
    "auc_factors": [ "CYP2D6" ],
    "status": "success",
    "message": "cyp3a4,oatp1b1,pgp",
    "ignored": "",
    "success": {
      "clarithromycin": {
        "auc_ratio": 8.47,
        "message": "cyp3a4,oatp1b1,pgp",
        "ignored": ""
      }
    },
    "failure": {}
  }
]
*/

import * as immu from 'object-path-immutable'
import { questions } from '../view-advice-kinetic/questions.js'
import * as flow from '../view-findings/flow.js'
import cloneDeep from 'lodash/cloneDeep'
// @rules - condensed rule object
// If input we don't persist and return updated input
// xeon with drugs, faqs, rules
export function kinetic( xeon ) {

  return async ( dispatch, getState, api ) => {

    const xeonState = xeon ? cloneDeep( xeon ) : getState().xeon

    // Get ids from drugs
    var ids = Object.keys( xeonState.drugs )

    // Nothing to predict in this case
    if ( ids.length < 1 ) return xeonState

    // ----------------------------------------------------------------------------------------------------------------------------
    // Add more infos from questions to the ids of drugs
    // https://api.epha.io/kinetic/predict/
    // ?id=aripiprazol,triazolam,rivaroxaban,diltiazem,erythromycin,grapefruit_dbs,CYP2D6*PM
    // ----------------------------------------------------------------------------------------------------------------------------
    const todos = [
      [ "grapefruit", "grapefruit_dbs" ],
      [ "rifampicin", "rifampicin_ss" ]
    ]

    // @TODO Special Cases we should ask questions
    ids = ids.map( id => {
      for ( const [ tid, tod ] of todos ) {
        if ( id == tid ) return tod
      }
      return id
    } )

    // Case: CYP2D6*PM
    const { cytochrome = {} } = xeonState.faqs

    for ( const key of Object.keys( cytochrome ) ) {
      const value = cytochrome[ key ]
      // Check if relevant status and transform to hayk format
      if ( value && /pm|em|im|um/.test( value ) ) {
        ids.push( `${key}*${value}`.toUpperCase() )
      }
      // console.log( key, value )
    }

    // @TODO Move to relevant view
    // Inform interested view about loading
    // dispatch( { type: "view.loading", data: true } )

    // ---------------------------------
    /// Update rules with predict object
    // ---------------------------------
    const { rules } = xeonState

    try {

      const { meta, data } = await api.kinetic.predict( ids )

      if ( meta.code != 200 ) {
        console.log( "> Predict failed", meta )
        if ( process.env.NODE_ENV == "develop" ) {
          console.log( data )
        }
        return xeonState
      }

      const result = data.reduce( ( acc, cur ) => {
        // Correct for ids above
        for ( const [ tid, tod ] of todos ) {
          if ( cur.drug == tod ) {
            cur.drug = tid
          }
          if ( cur.success && cur.success[ tod ] ) {
            cur.success[ tid ] = { ...cur.success[ tod ] }
            delete cur.success[ tod ]
          }
          if ( cur.failure && cur.failure[ tod ] ) {
            cur.failure[ tid ] = { ...cur.failure[ tod ] }
            delete cur.failure[ tod ]
          }
        }
        return immu.set( acc, cur.drug, cur )
      }, {} )

      // Iterate drugs
      for ( const drug in xeonState.drugs ) {
        // Find kinetic rule for drug
        const ruleId = findRuleId( drug, rules )
        // Ensure rule
        rules[ ruleId ] = rules[ ruleId ] || {
          group: "kinetic",
          drug: drug,
          conditions: [],
          param: [],
          modif: [],
          pubmed: []
        }
        // Success
        if ( result[ drug ] ) {
          rules[ ruleId ][ "predict" ] = result[ drug ]
        }
        // Missing or fail
        if ( !result[ drug ] ) {
          rules[ ruleId ][ "predict" ] = {
            drug: drug,
            auc_ratio: null,
            success: {},
            failure: {},
            status: "failure"
          }
        }
      } // Iterate drugs

    } catch ( e ) {
      console.log( "> Predict failed", e.message )
    }

    // -------------------------------------------
    // Update additional questions from kinetic
    // based on rules if not preflight computation
    // -------------------------------------------
    if ( !xeon ) {
      dispatch( { type: "xeon.rules", data: rules } )

      questions( xeonState ).forEach( key => {
        xeonState.faqs = flow.setKey( xeonState.faqs, key )
      } )

      dispatch( { type: "xeon.faqs", data: xeonState.faqs } )
    }

    // ----------------------------------------------
    // Return status for preflight computation
    // ----------------------------------------------
    return {
      drugs: xeonState.drugs,
      faqs: xeonState.faqs,
      // With updates of kinetic
      rules: rules
    }
  }

}


// ---------------------------------------------------------
// Find correct kinetic rule id for drug
// ---------------------------------------------------------
function findRuleId( drug, rules ) {

  const ruleKinetic = Object.keys( rules ).filter( id => {
    return id.includes( drug ) && id.includes( "kinetic" )
  } )

  if ( ruleKinetic.length == 1 ) {
    return ruleKinetic[ 0 ]
  }

  // Double kinetic rule
  if ( ruleKinetic.length > 1 ) {
    if ( process.env.NODE_ENV == "develop" ) {
      console.log( "Error: Found many kinetic rules for drug", drug )
      console.log( ruleKinetic )
      return ruleKinetic.slice( 0 ).pop()
    }
  }

  // Missing rule kinetic
  if ( process.env.NODE_ENV == "develop" ) {
    console.log( "Error: Missing kinetic rule for drug", drug )
  }

  // Fail save
  return `${drug}-kinetic-missing`

}